// ══════════════════════════════════════════════════════════════
// COMPASS – Browser-provided display names (Intl.DisplayNames)
// ══════════════════════════════════════════════════════════════
import { activeLocale } from '../i18n/active-locale';

type DisplayNamesType = 'region' | 'currency';

const cache = new Map<string, Intl.DisplayNames | null>();

function getDisplayNames(type: DisplayNamesType): Intl.DisplayNames | null {
  const locale = activeLocale();
  const key = `${locale}|${type}`;
  if (!cache.has(key)) {
    let instance: Intl.DisplayNames | null = null;
    try {
      instance = new Intl.DisplayNames([locale], { type, fallback: 'none' });
    } catch {
      // Unsupported locale/browser — callers fall back to their static names.
    }
    cache.set(key, instance);
  }
  return cache.get(key) ?? null;
}

function resolve(type: DisplayNamesType, code: string): string | undefined {
  try {
    return getDisplayNames(type)?.of(code) || undefined;
  } catch {
    // `of()` throws on malformed codes.
    return undefined;
  }
}

/** Localised region name for an ISO 3166-1 alpha-2 code, e.g. "PT" → "Portugal". */
export function getRegionDisplayName(alpha2: string): string | undefined {
  return resolve('region', alpha2);
}

/** Localised currency name for an ISO 4217 code, e.g. "EUR" → "Euro". */
export function getCurrencyDisplayName(code: string): string | undefined {
  const name = resolve('currency', code);
  // Some locales return lower-case names (pt: "euro"); capitalise for list display.
  return name ? name.charAt(0).toLocaleUpperCase(activeLocale()) + name.slice(1) : undefined;
}
